import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TableModule } from 'primeng/table';
import { filter, finalize, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';
import { RedTeamHttpService } from 'src/app/services/httpServices/redTeamHttpService/red-team-http.service';
import { IRedTeamSession } from 'src/app/models/api/red-team/red-team-session.model';
import { RedTeamSessionStatus } from 'src/app/models/api/red-team/red-team.enums';
import { SimulationStatusBadgeComponent } from '../simulation-status-badge/simulation-status-badge.component';
import {
  CreateAssessmentDialogComponent,
  CreateAssessmentResult,
} from '../create-assessment-dialog/create-assessment-dialog.component';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';

const AI_SERVICE_DISPLAY: Record<string, string> = {
  m365Copilot: 'Copilot for Microsoft 365',
};

@Component({
  selector: 'app-simulations-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    TableModule,
    LabelsProviderPipe,
    LucideIconsModule,
    SimulationStatusBadgeComponent,
  ],
  templateUrl: './simulations-list.component.html',
  styleUrl: './simulations-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimulationsListComponent implements OnInit {
  private readonly _redTeamHttp = inject(RedTeamHttpService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _logger = inject(LoggerService);
  private readonly _dialog = inject(MatDialog);
  private readonly _notifications = inject(NotificationsService);
  private readonly _sidePanel = inject(SidePanelService);

  readonly sessions = signal<IRedTeamSession[]>([]);
  readonly isLoading = signal(true);
  readonly searchTerm = signal('');

  readonly filteredSessions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const all = this.sessions();
    const filtered = term
      ? all.filter(
          (s) =>
            this.getAssessmentName(s).toLowerCase().includes(term) ||
            this.formatAttackStrategy(s.attackStrategy).toLowerCase().includes(term)
        )
      : all;
    return filtered.map((s) => ({
      ...s,
      resultsSortValue: this._getResultsSortValue(s),
    }));
  });

  readonly totalCount = computed(() => this.filteredSessions().length);

  ngOnInit(): void {
    this._loadSessions();
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  getAiServiceDisplay(aiService: string | null): string {
    if (!aiService) {
      return '-';
    }
    return AI_SERVICE_DISPLAY[aiService] ?? aiService;
  }

  getResultsDisplay(session: IRedTeamSession): string {
    if (session.status === RedTeamSessionStatus.Pending || session.status === RedTeamSessionStatus.Executing) {
      return '-';
    }
    if (session.status === RedTeamSessionStatus.FinishedFail) {
      return '-';
    }
    const count = session.detectedIssues?.length ?? 0;
    if (count === 0) {
      return 'No findings';
    }
    return `${count} ${count === 1 ? 'finding' : 'findings'}`;
  }

  getAssessmentName(session: IRedTeamSession): string {
    return session.attackName ?? '-';
  }

  formatAttackStrategy(attackName: string | null): string {
    if (!attackName) {
      return '-';
    }
    return attackName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  isFinished(session: IRedTeamSession): boolean {
    return (
      session.status === RedTeamSessionStatus.FinishedSuccess || session.status === RedTeamSessionStatus.FinishedFail
    );
  }

  isFailed(session: IRedTeamSession): boolean {
    return session.status === RedTeamSessionStatus.FinishedFail;
  }

  hasResults(session: IRedTeamSession): boolean {
    return this.isFinished(session) && (session.detectedIssues?.length ?? 0) > 0;
  }

  onRowClick(session: IRedTeamSession): void {
    this._sidePanel.openRedTeamSessionDetails(session);
  }

  onCreateAssessment(): void {
    const dialogRef = this._dialog.open(CreateAssessmentDialogComponent, {
      width: '640px',
      maxHeight: 'min(800px, 90vh)',
      panelClass: 'popup-dialog-container',
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is CreateAssessmentResult => !!result),
        switchMap((result) =>
          this._redTeamHttp.createSession({
            attackName: result.testName,
            context: result.customContext ?? result.attackStrategy,
            config: {
              aiService: 'm365Copilot',
              authCode: result.authCode,
            },
          })
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._notifications.success('Assessment created successfully');
          this._loadSessions();
        },
        error: (err) => {
          this._logger.error('Failed to create assessment', err);
        },
      });
  }

  private _getResultsSortValue(session: IRedTeamSession): number {
    if (!this.isFinished(session)) {
      return -1;
    }
    return session.detectedIssues?.length ?? 0;
  }

  private _loadSessions(): void {
    this.isLoading.set(true);
    this._redTeamHttp
      .getSessions()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (sessions) => this.sessions.set(sessions),
        error: (err) => this._logger.error('Failed to load red team sessions', err),
      });
  }
}
